import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { Button, Card, CardTitle, Chip, Field, Input, Modal } from '@/ui/primitives';
import { Stack } from '@/ui/layout';
import { ApiError } from '@/lib/api-client';
import { NIVEL_LABEL, nivelTone } from '../data';
import { useConsultaAvulsa } from '../api';
import { VerificationRow } from './VerificationRow';

interface Props {
  open: boolean;
  onClose: () => void;
}

function digitsOnly(s: string): string {
  return s.replace(/\D/g, '');
}

function isValidCpf(s: string): boolean {
  return digitsOnly(s).length === 11;
}

export function ConsultaAvulsaModal({ open, onClose }: Props) {
  const [cpf, setCpf] = useState('');
  const consulta = useConsultaAvulsa();

  useEffect(() => {
    if (!open) {
      setCpf('');
      consulta.reset();
    }
  }, [open, consulta]);

  const onSubmit = () => {
    const clean = digitsOnly(cpf);
    if (!isValidCpf(clean)) return;
    consulta.mutate(clean);
  };

  const result = consulta.data;
  const showInput = !result;

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="Consulta avulsa por CPF"
      size="lg"
      footer={
        showInput ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={onSubmit}
              disabled={consulta.isPending || !isValidCpf(cpf)}
            >
              <Search size={14} strokeWidth={1.5} />
              {consulta.isPending ? 'Consultando…' : 'Consultar'}
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={() => consulta.reset()}>
              Nova consulta
            </Button>
            <Button variant="primary" onClick={onClose}>
              Fechar
            </Button>
          </>
        )
      }
    >
      {showInput ? (
        <Stack gap="md">
          <p className="text-sm text-text-2">
            Consulta CNDT e IBAMA pra um CPF que pode não estar na carteira. Roda em ~30s.
          </p>
          <Field label="CPF" htmlFor="consulta-avulsa-cpf">
            <Input
              id="consulta-avulsa-cpf"
              value={cpf}
              onChange={(e) => setCpf(e.target.value)}
              placeholder="Apenas dígitos"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSubmit();
              }}
            />
          </Field>
          {consulta.error && (
            <p className="text-xs text-danger">
              {consulta.error instanceof ApiError
                ? consulta.error.message
                : 'Falha na consulta'}
            </p>
          )}
        </Stack>
      ) : (
        <Stack gap="md">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-text-muted font-mono">{result.cpf}</span>
            {result.nome && <span className="text-sm text-text">{result.nome}</span>}
            {result.nivel && (
              <Chip tone={nivelTone(result.nivel)} size="sm" dot>
                {NIVEL_LABEL[result.nivel]}
                {result.score != null ? ` · ${result.score.toFixed(0)}%` : ''}
              </Chip>
            )}
          </div>
          <Card padding="md">
            <Stack gap="sm">
              <CardTitle>Verificações</CardTitle>
              {result.verificacoes.length === 0 ? (
                <p className="text-xs text-text-muted">Nenhuma verificação retornada.</p>
              ) : (
                <ul className="flex flex-col">
                  {result.verificacoes.map((v, i) => (
                    <VerificationRow
                      key={`a-${v.verificacao}-${i}`}
                      verification={v}
                      cpf={result.cpf}
                      imovelId={null}
                    />
                  ))}
                </ul>
              )}
            </Stack>
          </Card>
        </Stack>
      )}
    </Modal>
  );
}
